import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import Grid from '@mui/material/Grid';

import { useCallback, useEffect, useState } from 'react';
import { ForecastFilter } from 'common/models/Reporting/AnalyticsFilterData';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import SettingsIcon from '@mui/icons-material/Settings';
import { NameIdObj } from 'common/models/GenericTypes';
import Thermometer from './Thermometer/ThermometerComponent';
import { GetForecastDataForGraph } from 'services/ReportingService';
import { ForecastGraphData } from 'common/models/Reporting/Forecasts';
import { GetReportingSettings } from 'services/ConfigurationService';

interface Props {
  id: string;
  title: string;
  sourceType: string;
  selectedIds: string;
  reportPeriod: number;
  showConfigButton?: boolean;
  kioskToken?: string | null;
  refreshFlag?: boolean | null;
  saveConfigHandler?: (
    id: string,
    title: string,
    userFilterIds: string,
    sourceType: string,
    reportPeriod: number
  ) => void;
}

export default function ForecastGraph({
  id,
  title: titleProp,
  kioskToken = null,
  refreshFlag = null,
  sourceType: sourceTypeProps,
  selectedIds,
  reportPeriod: reportPeriodProp,
  showConfigButton,
  saveConfigHandler,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState<string>('');
  const [color, setColor] = useState<string>('orange');
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [data, setData] = useState<ForecastGraphData | null>(null);
  const [graphTotal, setGraphTotal] = useState<number>(0);
  const [reportingPeriod, setReportingPeriod] = useState(0);
  const [errorHandler, setErrorHandler] = useState<string>('');
  const [targetLabelOverride, setTargetLabelOverride] = useState('Target');
  const [stretchLabelOverride, setStretchLabelOverride] = useState('Stretch');

  const getMaxValue = (data: ForecastGraphData) => {
    return Math.max(data.total, data.target, data.stretch);
  };
  useEffect(() => {
    setTitle(titleProp);
    setReportingPeriod(reportPeriodProp);
    setSourceType(sourceTypeProps);
    if (selectedIds) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(selectedIds.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(selectedIds.split(',').map(Number));
      }
    }
  }, [sourceTypeProps, selectedIds, reportPeriodProp, titleProp]);

  const getData = async () => {
    setIsLoading(true);
    let filterTypeId = 0;
    if (sourceType == 'consultants') {
      filterTypeId = 4;
    } else if (sourceType == 'groups') {
      filterTypeId = 3;
    } else if (sourceType == 'currentuser') {
      filterTypeId = 5;
    } else {
      filterTypeId = 1;
    }

    if (sourceType != '' && reportingPeriod != 0) {
      const filter = {
        userFilterID: selectedIds,
        userFilterTypeID: filterTypeId,
        reportPeriod: reportingPeriod,
      } as ForecastFilter;
      const res = await GetForecastDataForGraph(filter, kioskToken, setErrorHandler);

      const settingsRes = await GetReportingSettings(kioskToken);
      if (settingsRes) {
        for (let i = 0; i < settingsRes.length; i++) {
          const setting = settingsRes[i];
          if (setting.type === 'TargetLabelOverride' && setting.value)
            setTargetLabelOverride(setting.value);
          if (setting.type === 'StretchLabelOverride' && setting.value)
            setStretchLabelOverride(setting.value);
        }
      }

      if (res) {
        setData(res);
        setGraphTotal(getMaxValue(res));

        if (res.total < res.target) {
          setColor('red');
        } else if (res.total > res.target && res.total < res.stretch) {
          setColor('orange');
        } else if (res.total > res.stretch) {
          setColor('green');
        }
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [reportingPeriod, kioskToken]);

  useEffect(() => {
    if (refreshFlag && kioskToken) {
      getData();
    }
  }, [refreshFlag, kioskToken]);

  const mapIdsToString = useCallback(() => {
    let selectedIds = '';
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers.join(',');
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups.join(',');
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(() => {
    saveConfigHandler &&
      saveConfigHandler(id, title, mapIdsToString(), sourceType, reportingPeriod);
    setShowConfigDialog(false);
  }, [saveConfigHandler, selectedUsers, selectedGroups, sourceType, title, id, reportingPeriod]);

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  const onNumericValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const handleCancel = () => {
    setShowConfigDialog(false);
    setTitle(titleProp);
    setReportingPeriod(reportPeriodProp);
    setSourceType(sourceTypeProps);
    if (selectedIds) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(selectedIds.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(selectedIds.split(',').map(Number));
      }
    }
  };

  return (
    <>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
            <TextField
              select
              label="Reporting Period"
              value={reportingPeriod.toString()}
              onChange={({ target }) => setReportingPeriod(+target.value)}
              fullWidth
            >
              <MenuItem value="1">This Month</MenuItem>
              <MenuItem value="2">Next Month</MenuItem>
              <MenuItem value="3">This Quarter</MenuItem>
              <MenuItem value="4">Next 3 Months</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showConfigButton && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          p: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
          {title}
        </Typography>
        {errorHandler != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorHandler}
          </Typography>
        )}
        {isLoading && (
          <Box m="auto">
            <CircularProgress />
          </Box>
        )}
        {!isLoading && errorHandler == '' && (
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={7}>
                <List
                  sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
                  aria-label="contacts"
                >
                  <ListItem disablePadding>
                    <Typography variant="h6" textAlign="center" fontWeight={600} component="div">
                      Open Jobs
                    </Typography>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      style={{ paddingLeft: 10 }}
                      primary={`Permanent - ${Intl.NumberFormat('en-US').format(Math.round(data?.openJobsPerm ?? 0))}`}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      style={{ paddingLeft: 10 }}
                      primary={`Contract - ${Intl.NumberFormat('en-US').format(Math.round(data?.openJobsEstimated ?? 0))}`}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <Typography variant="h6" textAlign="center" fontWeight={600} component="div">
                      Revenue
                    </Typography>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      style={{ paddingLeft: 10 }}
                      primary={`Permanent - ${Intl.NumberFormat('en-US').format(Math.round(data?.revenuePermanent ?? 0))}`}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      style={{ paddingLeft: 10 }}
                      primary={`Contract Actuals - ${Intl.NumberFormat('en-US').format(Math.round(data?.revenueContractActuals ?? 0))}`}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      style={{ paddingLeft: 10 }}
                      primary={`Contract Estimated - ${Intl.NumberFormat('en-US').format(Math.round(data?.revenueContractEstimated ?? 0))}`}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={5}>
                <Thermometer
                  id={id}
                  color={color}
                  stretch={Math.round(data?.stretch ?? 0)}
                  target={Math.round(data?.target ?? 0)}
                  theme="light"
                  value={Math.round(data?.total ?? 0)}
                  max={Math.round(graphTotal)}
                  size="normal"
                  height={280}
                  targetLabel={targetLabelOverride}
                  stretchLabel={stretchLabelOverride}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" alignItems="center"></Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </>
  );
}
